import React, { createContext, useContext, useState, useEffect } from 'react';
import type { Product } from '../types/storefront';

export interface CartItem extends Product {
    quantity: number;
    selected_variants?: Record<string, string>;
    cartItemId: string; // Unique ID for the cart line item (id + variants)
}

interface CartContextType {
    items: CartItem[];
    addToCart: (product: Product, variants?: Record<string, string>) => void;
    removeFromCart: (cartItemId: string) => void;
    updateQuantity: (cartItemId: string, quantity: number) => void;
    clearCart: () => void;
    totalAmount: number;
    totalCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
    const [items, setItems] = useState<CartItem[]>([]);

    // Initialize cart from localStorage
    useEffect(() => {
        const savedCart = localStorage.getItem('moniflow_cart');
        if (savedCart) {
            try {
                setItems(JSON.parse(savedCart));
            } catch (e) {
                console.error("Failed to parse cart from localStorage", e);
            }
        }
    }, []);

    // Save cart to localStorage
    useEffect(() => {
        localStorage.setItem('moniflow_cart', JSON.stringify(items));
    }, [items]);

    const addToCart = (product: Product, selectedVariants?: Record<string, string>) => {
        setItems(prev => {
            // Generate a unique cart item ID based on the product ID and selected variants
            const variantString = selectedVariants
                ? Object.entries(selectedVariants).sort((a, b) => a[0].localeCompare(b[0])).map(v => `${v[0]}_${v[1]}`).join('-')
                : '';
            const cartItemId = variantString ? `${product.id}-${variantString}` : product.id;

            const existing = prev.find(item => item.cartItemId === cartItemId);
            const currentQty = existing ? existing.quantity : 0;

            // Stock check
            if (product.track_inventory && currentQty >= product.stock_quantity) {
                return prev;
            }

            if (existing) {
                return prev.map(item =>
                    item.cartItemId === cartItemId
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { ...product, quantity: 1, selected_variants: selectedVariants, cartItemId }];
        });
    };

    const removeFromCart = (cartItemId: string) => {
        setItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
    };

    const updateQuantity = (cartItemId: string, quantity: number) => {
        if (quantity < 1) {
            removeFromCart(cartItemId);
            return;
        }
        setItems(prev => prev.map(item => {
            if (item.cartItemId === cartItemId) {
                const finalQty = item.track_inventory ? Math.min(quantity, item.stock_quantity) : quantity;
                return { ...item, quantity: finalQty };
            }
            return item;
        }));
    };

    const clearCart = () => {
        setItems([]);
        localStorage.removeItem('moniflow_cart');
    };

    const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalCount = items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider value={{
            items,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            totalAmount,
            totalCount
        }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}
